import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  savePostAsDraftThunk,
  updatePostSocialsThunk,
  schedulePostThunk,
  getPostThunk,
  getAllowedSocialTypesThunk,
  getPostApprovalInfoThunk,
  showAllPostSocialNoteThunk,
  addMediaToTemporaryMediasThunk,
  deleteMediaFromTemporaryMediasThunk,
  getTemporaryMediasThunk,
  savePostSocialNoteThunk,
  deletePostSocialNoteThunk,
  updateSocialPostNoteThunk,
  requireApproverRevisionThunk,
  addStatusesToCheckboxesInRevisionBlockThunk,
  pushQuestionByContentCreatorThunk,
  pushAnswerByApproverThunk,
  startNextApprovalRoundThunk,
  approveCurrentRoundThunk,
  unApproveCurrentRoundThunk,
  importTemporaryMediaFromCanva,
  checkMediaRestrictionsThunk,
  publishPostManuallyThunk,
  checkMediaRestrictionsForCopyDataToSocialPostsThunk,
  adminPublishPostManuallyThunk,
  updateScheduleThunk,
  removeAttachmentThunk
} from './thunks'
import { postApprovalPostsThunk } from '../modal/approvalPost/thunks'

import { getPostsForApprovalThunk } from '../project/thunks'
import {
  ACTION_TYPE,
  APPROVAL_TYPE,
  ATTACHMENT_TYPE,
  MEDIA_DATA_TYPE,
  POST_SOCIAL_STATUS,
  POST_STATUS,
  POST_TYPE,
  SOCIAL_NETWORK,
  TApprovalAction,
  TPostSocial,
  TAttachmentFacebookVideoTag,
  ATTACHMENT_DESCRIPTION_TYPE,
  ATTACHMENT_EXTRAFIELD,
  TcompletePostDetails
} from '../../models/post'
import {
  IManagePostState,
  TAddImageToLinkAttachmentPayload,
  TAddLinkToSocialAttachmentPayload,
  TAddVideoThumbnailAttachmentPayload,
  TAttachmentIds,
  TChangeAttachmentExtrafieldPayload,
  TFieldChangePayload,
  TPostSocialManagePost,
  TPostSocialsActionsMapByPostSocialId,
  TRemoveAttachmentPayload,
  TSetPostSocialExtrafieldPayload,
  VIEW_TYPE
} from './newTypes'
import { TPost } from './types'
import { batchSoftDeletePostsThunk, softDeletePostThunk } from '../calendar/thunks'

const initialState: IManagePostState = {
  viewType: VIEW_TYPE.TYPE_DESKTOP,
  post: {
    id: 0,
    project_id: 0,
    type: POST_TYPE.POST_TYPE_SINGLE_PHOTO,
    status: POST_STATUS.STATUS_DRAFT,
    message: '',
    first_comment: '',
    parent_post_id: 0,
    created_at: '',
    updated_at: '',
    deleted_at: '',
    master_social_post_id: 0,
    master_social_network: null,
    concrete_internal_approver: null,
    concrete_external_approver: null,
    approval_id:0,
    can_approve_post_socials:[],
    can_unapprove_post_socials: []
  },
  latestSchedule: {
    id: 0,
    is_autoposting: true,
    timezone: '',
    date: '',
    datetime: '',
    time: ''
  },
  postSocials: [],
  postActions: [],
  postSocialsApprovalActions: {},
  postSocialNotes: {},
  approval: {
    approval_plan: {
      postId: 0,
      isMasterContent: false,
      isSingleRoundInternalApprove: false,
      masterSocialNetwork: null,
      rounds: null
    },
    post_socials: [],
    current_user_id:0
  },
  loaders: {
    post: {
      save: false,
      loadingTypes: false,
      approve: false,
      delete: false,
      copyAttachments: false,
      skipApproval:false,
      processingIds: []
    },
    schedules: {
      delete: false,
      update: false
    },
    approval: {
      loading: false,
      requestRevision: false,
      saveQuestionOrAnswer: false,
      requestQty: 0
    },
    note: {
      save: false,
      delete: false,
      processingIds: []
    }
  },
  availablePostTypes: [],
  selectedSocials: [],
  selectedPostType: POST_TYPE.POST_TYPE_SINGLE_PHOTO,
  selectedSocial: SOCIAL_NETWORK.FACEBOOK_SOCIAL,
  selectedPostSocialId: 0,
  linkCarouselSelectedAttachmentIds: {
    id: 0,
    temporaryId: 0
  },
  selectedPostsToApproval: [],
  availableSocialNetworks: [],
  error: null,
  isGettingPostError: false,
  isExistChanges: false,
  postDetails:[],
}

const managePostSlice = createSlice({
  name: 'managePost',
  initialState,
  reducers: {
    setViewAction: (state, action: PayloadAction<VIEW_TYPE>) => {
      state.viewType = action.payload
    },
    setLinkCarouselSelectedAttachmentIdsAction: (state, action: PayloadAction<TAttachmentIds>) => {
      state.linkCarouselSelectedAttachmentIds = action.payload
    },
    setPostSocialExtrafieldsAction: (
      state,
      action: PayloadAction<TSetPostSocialExtrafieldPayload>
    ) => {
      const { social, mention } = action.payload
      const socialIndex = state.postSocials.findIndex(
        ({ social_network }) => social_network === social
      )

      if (socialIndex !== -1) {
        if (state.postSocials[socialIndex].extra_fields?.mentions) {
          state.postSocials[socialIndex].extra_fields.mentions?.push(mention)
        } else {
          state.postSocials[socialIndex].extra_fields = {
            ...state.postSocials[socialIndex].extra_fields,
            mentions: [mention]
          }
        }
      }
    },
    changeAttachmentExtrafieldAction: (
      state,
      action: PayloadAction<TChangeAttachmentExtrafieldPayload>
    ) => {
      const { field, value, id, temporaryId } = action.payload
      // TODO attachments link-carousel (? split with content tags)

      const postSocialIndex = state.postSocials.findIndex(
        ({ id }) => state.selectedPostSocialId === id
      )
      if (postSocialIndex !== -1) {
        state.isExistChanges = true
        state.postSocials[postSocialIndex].attachments = state.postSocials[
          postSocialIndex
        ].attachments.map((attachment) =>
          !(
            (!!temporaryId && temporaryId === attachment.temporaryId) ||
            (!!id && id === attachment.id)
          )
            ? attachment
            : {
              ...attachment,
              toUpdate: true,
              extra_fields: {
                ...attachment.extra_fields,
                [field]: value
              }
            }
        )
      }
    },
    addImageToLinkAttachmentAction: (
      state,
      action: PayloadAction<TAddImageToLinkAttachmentPayload>
    ) => {
      const { media, id, temporaryId } = action.payload
      const postSocialIndex = state.postSocials.findIndex(
        ({ id }) => state.selectedPostSocialId === id
      )
      if (postSocialIndex !== -1) {
        state.isExistChanges = true
        state.postSocials[postSocialIndex].attachments = state.postSocials[
          postSocialIndex
        ].attachments.map((attachment) =>
          (!!id && attachment.id === id) ||
            (!!temporaryId && temporaryId === attachment.temporaryId)
            ? {
              ...attachment,
              toDeatachMedia: true,
              object_data: media,
              object_id: media.id
            }
            : attachment
        )
      }
    },
    removeImageFromLinkAttachmentAction: (state, action: PayloadAction<TAttachmentIds>) => {
      const { id, temporaryId } = action.payload
      const postSocialIndex = state.postSocials.findIndex(
        ({ id }) => state.selectedPostSocialId === id
      )
      if (postSocialIndex !== -1) {
        state.isExistChanges = true
        state.postSocials[postSocialIndex].attachments = state.postSocials[
          postSocialIndex
        ].attachments.map((attachment) =>
          (!!id && attachment.id === id) ||
            (!!temporaryId && temporaryId === attachment.temporaryId)
            ? {
              ...attachment,
              object_data: null,
              object_id: 0,
              toDeatachMedia: true
            }
            : attachment
        )
      }
    },
    setSelectedPostSocialIdAction: (state, action: PayloadAction<number>) => {
      state.selectedPostSocialId = action.payload
    },
    addImageToStoreAction: (state, action: PayloadAction<MEDIA_DATA_TYPE>) => {
      const postSocialIndex = state.postSocials.findIndex(
        ({ id }) => state.selectedPostSocialId === id
      )
      const attachment = action.payload
      state.postSocials[postSocialIndex].attachments.push({
        id: 0,
        temporaryId: state.postSocials[postSocialIndex].attachments.length + 1,
        post_social_id: state.postSocials[postSocialIndex].id,
        object_data: attachment,
        object_id: attachment.id,
        description: ATTACHMENT_DESCRIPTION_TYPE.MEDIA,
        object_type: ATTACHMENT_TYPE.TYPE_IMAGE,
        thumbnail_object_data: null,
        thumbnail_object_id: null,
        preview_image_id: 0,
        created_at: '',
        updated_at: ''
      })
      state.isExistChanges = true
    },
    setSelectedSocialAction: (state, action: PayloadAction<SOCIAL_NETWORK>) => {
      const activeSocial = state.postSocials.find(
        (social) => social.social_network === action.payload
      )
      state.selectedSocial = action.payload
      state.selectedPostSocialId = activeSocial ? activeSocial.id : 0
    },

    setPostDetails:(state, action: PayloadAction<TcompletePostDetails>) => {
      const payloadValues=Object.values(action.payload);
      const postIdDetails =payloadValues.map((det)=>det['post']);
      let arrVal:any[]=[];
      const postInf = payloadValues.map(function(det){
        arrVal.push({'post_id':det['post']['id'],'post_social':det['post']['post_socials'][0]['social_network']});
      })
      state.postDetails = arrVal;
    },
    addFacebookVideoTagAction: (state, action: PayloadAction<TAttachmentFacebookVideoTag[]>) => {
      // TODO FBTAGS
      state.isExistChanges = true
      state.postSocials = state.postSocials.map((social) =>
        social.social_network !== SOCIAL_NETWORK.FACEBOOK_SOCIAL
          ? social
          : {
            ...social,
            attachments: social.attachments.map((attachment) =>
              attachment.toRemove
                ? attachment
                : {
                  ...attachment,
                  toUpdate: true,
                  extra_fields: {
                    ...attachment.extra_fields,
                    content_tags: action.payload
                  }
                }
            )
          }
      )
    },
    addThumbnailToAttachmentAction: (
      state,
      action: PayloadAction<TAddVideoThumbnailAttachmentPayload>
    ) => {
      const { id, temporaryId, media } = action.payload
      state.isExistChanges = true
      state.postSocials = state.postSocials.map((social) =>
        social.social_network !== SOCIAL_NETWORK.FACEBOOK_SOCIAL
          ? social
          : {
            ...social,
            attachments: social.attachments.map((attachment) =>
              (temporaryId && temporaryId === attachment.temporaryId) ||
                (id && attachment.id === id)
                ? {
                  ...attachment,
                  thumbnail_object_data: media,
                  thumbnail_object_id: media.id,
                  toAddVideoTumbnail: true,
                  toUpdateVideoTumbnail: !!attachment.toRemoveVideoTumbnail,
                  toRemoveVideoTumbnail: false
                }
                : attachment
            )
          }
      )
    },
    removeThumbnailFromAttachmentAction: (state, action: PayloadAction<TAttachmentIds>) => {
      const { id, temporaryId } = action.payload
      state.isExistChanges = true
      state.postSocials = state.postSocials.map((social) =>
        social.social_network !== SOCIAL_NETWORK.FACEBOOK_SOCIAL
          ? social
          : {
            ...social,
            attachments: social.attachments.map((attachment) =>
              (temporaryId && temporaryId === attachment.temporaryId) ||
                (id && attachment.id === id)
                ? {
                  ...attachment,
                  toAddVideoTumbnail: false,
                  toRemoveVideoTumbnail: true
                }
                : attachment
            )
          }
      )
    },
    addLinkToSocialAttachmentAction: (
      state,
      action: PayloadAction<TAddLinkToSocialAttachmentPayload>
    ) => {
      const { isChildLink } = action.payload
      const postSocialIndex = state.postSocials.findIndex(
        ({ id }) => state.selectedPostSocialId === id
      )
      if (postSocialIndex !== -1) {
        state.isExistChanges = true
        state.postSocials[postSocialIndex].attachments.push({
          id: 0,
          post_social_id: state.selectedPostSocialId,
          object_data: null,
          object_id: 0,
          temporaryId: state.postSocials[postSocialIndex].attachments.length + 1,
          object_type: ATTACHMENT_TYPE.LINK,
          description: isChildLink
            ? ATTACHMENT_DESCRIPTION_TYPE.CHILD_LINK
            : ATTACHMENT_DESCRIPTION_TYPE.LINK,
          preview_image_id: 0,
          thumbnail_object_data: null,
          thumbnail_object_id: null,
          created_at: '',
          updated_at: '',
          extra_fields: {
            is_child_link: isChildLink,
            link: '',
            title: ''
          }
        })
      }
    },
    setSelectedSocialsAction: (state, action: PayloadAction<SOCIAL_NETWORK[]>) => {
      state.selectedSocials = action.payload
    },

    setSelectedPostsToApproveAction: (state, action: PayloadAction<number[]>) => {
      state.selectedPostsToApproval = action.payload
    },
    //   setIsPostSocialEditableAction: (
    //     state,
    //     action: PayloadAction<{ social_network: Socials; isEditable: boolean }>
    //   ) => {
    //     const { social_network, isEditable } = action.payload
    //     state.postSocials[social_network] = {
    //       ...state.postSocials[social_network],
    //       isEditable: isEditable
    //     }
    //   },
    setEmptyPostSocialsAction: (state, action: PayloadAction<TPostSocialManagePost[]>) => {
      state.postSocials = action.payload
      state.post.status = POST_STATUS.STATUS_DRAFT
    },
    setPostTypeAction: (state, action: PayloadAction<POST_TYPE>) => {
      state.selectedPostType = action.payload
    },
    onFieldChangeAction: (state, action: PayloadAction<TFieldChangePayload>) => {
      const { socialNetwork, field, value, rawValue } = action.payload
      const postSocialIndex = state.postSocials.findIndex(
        ({ social_network }) => socialNetwork === social_network
      )
      state.isExistChanges = true
      state.postSocials[postSocialIndex] = {
        ...state.postSocials[postSocialIndex],
        [field]: value,
        message_editor_state: rawValue || state.postSocials[postSocialIndex].message_editor_state
      }
    },
    //   uploadImagesAction: (state, action: PayloadAction<TImagesChangePayload>) => {
    //     state.postSocials[action.payload.social].attachments = action.payload.images
    //   },
    setIsSchedulingPostAction: (state, action: PayloadAction<boolean>) => {
      state.loaders.schedules.update = action.payload
    },
    removeAttachmentFromListAction: (state, action: PayloadAction<TRemoveAttachmentPayload>) => {
      const socialIndex = state.postSocials.findIndex(
        (socialPost) => socialPost.id === state.selectedPostSocialId
      )
      if (socialIndex !== -1) {
        state.isExistChanges = true
        state.postSocials[socialIndex].attachments = state.postSocials[socialIndex].attachments.map(
          (media) =>
            (!!action.payload.id && media.id === action.payload.id) ||
              (!!action.payload.temporaryId && media.temporaryId === action.payload.temporaryId)
              ? { ...media, toRemove: true }
              : media
        )
      }
    },
    setDateAction: (state, action: PayloadAction<string>) => {
      state.latestSchedule.date = action.payload
    },
    setTimeAction: (state, action: PayloadAction<string>) => {
      state.latestSchedule.time = action.payload
    },
    setScheduleIdAction: (state, action: PayloadAction<number>) => {
      state.latestSchedule.id = action.payload
    },
    toggleIsAutopostingAction: (state) => {
      state.latestSchedule.is_autoposting = !state.latestSchedule.is_autoposting
    },
    setIsAutopostingAction: (state, action: PayloadAction<boolean>) => {
      state.latestSchedule.is_autoposting = action.payload
    },
    setPostToApproveAction: (state, action: PayloadAction<any>) => {
      if (action.payload.id) {
        const { latest_schedule, post_socials } = action.payload
        state.latestSchedule = latest_schedule
        state.postSocials = post_socials
        state.selectedPostSocialId = post_socials[0].id
        state.selectedSocial = post_socials[0].social_network
      }
      state.post = action.payload
    },
    //   setPostStatusAction: (state, action: PayloadAction<any[]>) => {
    //     for (let [social, socialData] of Object.entries(state.postSocials)) {
    //       action.payload.map((socialsApproval: any) => {
    //         if (socialsApproval.id === socialData.id) {
    //           state.postSocials[social as Socials].status = socialsApproval.status
    //         }
    //       })
    //     }
    //   },
    resetManagePostStateAction: (state) => initialState,

    updateSkipApprovalAction: (state,action:PayloadAction<any>)=>{
      state.post.status = action.payload.post_status
      state.postSocials = action.payload.post_socials
    }
  },
  extraReducers: (builder) => {
    //   // Create post as a draft
    builder.addCase(savePostAsDraftThunk.pending, (state, action) => {
      state.loaders.post.save = true
    })
    builder.addCase(savePostAsDraftThunk.fulfilled, (state, action) => {
      const { post_socials }: { post_socials: TPostSocialManagePost[] } = action.payload.data
      const post = action.payload.data
      delete post.post_socials

      state.isExistChanges = false
      state.post = post
      state.postSocials.forEach(({ social_network }, index) => {
        const { id } = post_socials.find(
          ({ social_network: social_network_from_response }) =>
            social_network === social_network_from_response
        )!
        state.postSocials[index] = { ...state.postSocials[index], id }
      })
      state.loaders.post.save = false
    })
    builder.addCase(savePostAsDraftThunk.rejected, (state, action) => {
      state.loaders.post.save = false
      state.error = action.payload
    })
    //   // update separate social
    builder.addCase(updatePostSocialsThunk.pending, (state, action) => {
      state.loaders.post.save = true
    })
    builder.addCase(updatePostSocialsThunk.fulfilled, (state, action: PayloadAction<TPost>) => {
      const updatedPost = action.payload
      const postSocialsData = updatedPost.post_socials.reduce((acc, postSocial) => {
        return {
          ...acc,
          [postSocial.social_network]: postSocial
        }
      }, {} as { [key in SOCIAL_NETWORK]: TPostSocialManagePost & { attachmentIds: number[] } })

      state.postSocials = state.postSocials.map((socialPost) => {
        const updatedSocialPost = postSocialsData[socialPost.social_network]
        return {
          ...updatedSocialPost,
          attachments: updatedSocialPost.attachments.map((updatedAttachment, index) => {
            const findedStoreAttachment = socialPost.attachments.find((storeAttachment) => {
              return (
                storeAttachment.object_data?.parent_media_id ===
                updatedAttachment.object_data?.parent_media_id ||
                storeAttachment.object_data?.id === updatedAttachment.object_data?.parent_media_id
              )
            })
            return {
              ...updatedAttachment,
              object_data: findedStoreAttachment?.object_data || updatedAttachment.object_data
            }
          })
        }
      })
      const { id } = updatedPost.post_socials.find(
        ({ social_network }: { social_network: SOCIAL_NETWORK }) =>
          social_network === state.selectedSocial
      )!
      state.isExistChanges = false
      state.selectedPostSocialId = id
      state.loaders.post.save = false
    })
    builder.addCase(updatePostSocialsThunk.rejected, (state, action) => {
      state.loaders.post.save = false
      state.error = action.payload
    })
    //   // create shcedule
    builder.addCase(schedulePostThunk.pending, (state, action) => { })
    builder.addCase(schedulePostThunk.fulfilled, (state, action) => {
      const { id, timezone, date, time, is_autoposting, datetime } = action.payload.data
      state.latestSchedule = {
        id,
        timezone,
        time,
        date,
        is_autoposting,
        datetime
      }
    })
    builder.addCase(schedulePostThunk.rejected, (state, action) => {
      state.error = action.payload
    })
    // update single schedule
    builder.addCase(updateScheduleThunk.fulfilled, (state, action) => {
      state.latestSchedule = action.payload.post.latest_schedule
    })
    //   // update schedules
    //   builder.addCase(updateSchedulesThunk.pending, (state, action) => {})
    //   builder.addCase(updateSchedulesThunk.fulfilled, (state, action) => {})
    //   builder.addCase(updateSchedulesThunk.rejected, (state, action) => {
    //     state.error = action.payload
    //   })
    //   // get allowed post types
    builder.addCase(getAllowedSocialTypesThunk.pending, (state, action) => {
      state.loaders.post.loadingTypes = true
    })
    builder.addCase(getAllowedSocialTypesThunk.fulfilled, (state, action) => {
      state.availablePostTypes = Object.keys(action.payload.data) as POST_TYPE[]
      state.loaders.post.loadingTypes = false
    })
    builder.addCase(getAllowedSocialTypesThunk.rejected, (state, action) => {
      state.loaders.post.loadingTypes = false
      state.error = action.payload
    })
    //   // get post
    builder.addCase(getPostThunk.pending, (state, action) => { })
    builder.addCase(getPostThunk.fulfilled, (state, action) => {
      const { post_socials, id, latest_schedule, type } = action.payload.data
      const post = action.payload.data
      delete post.post_socials
      delete post.latest_schedule

      state.isExistChanges = false
      state.postSocials = post_socials
      state.post = post
      state.selectedSocials = post_socials.map(
        (socialPost: TPostSocialManagePost) => socialPost.social_network
      )
      state.latestSchedule = latest_schedule
      state.selectedPostType = type
      state.selectedPostSocialId = post_socials[0].id
      const masterSocialNetwork = state.approval.approval_plan.masterSocialNetwork
      if (!state.selectedSocial) {
        state.selectedSocial = masterSocialNetwork
          ? masterSocialNetwork
          : post_socials[0].social_network
      }
    })
    builder.addCase(getPostThunk.rejected, (state, action) => {
      state.isGettingPostError = true
    })
    // Get full post approval info
    builder.addCase(getPostApprovalInfoThunk.pending, (state, action) => {
      if (!state.loaders.approval.loading) {
        state.loaders.approval.loading = true
      }
      state.loaders.approval.requestQty = state.loaders.approval.requestQty + 1
    })
    builder.addCase(getPostApprovalInfoThunk.fulfilled, (state, action) => {
      state.loaders.approval.requestQty = state.loaders.approval.requestQty - 1
      if (state.loaders.approval.requestQty !== 0) return

      state.loaders.approval.loading = false
      state.approval.approval_plan = action.payload.data.approval_plan
      state.post.concrete_external_approver = action.payload.data.concrete_external_approver
      state.post.concrete_internal_approver = action.payload.data.concrete_external_approver
      state.approval.post_socials = action.payload.data.post_socials
      state.approval.current_user_id = action.payload.data.current_user_id


      const { isMasterContent, masterSocialNetwork } = state.approval.approval_plan

      if (isMasterContent) {
        const socialPost = state.postSocials.find(
          (socialPost) => socialPost.social_network === masterSocialNetwork
        )
        const { id, approvals,  status:postSocialStatus } = state.approval.post_socials.find(
          ({ is_master_content: isMasterContent, id }) => isMasterContent || socialPost?.id === id
        )!
        const approvalActionsByRounds = approvals.reduce(
          (
            accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
            { actions, type }
          ) => {
            // define start external approve
            const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
              actions.map(action => ({
                ...action,
                type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                  ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                  action.type
              }))
            accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
            return accPostSocialActions
          },
          { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
        )

        const questionAnswerHistoryByApprovalId = approvals.reduce(
          (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
            const questionAnswerHistory: TApprovalAction[] = actions.filter(
              ({ type }) =>
                type === ACTION_TYPE.TYPE_ANSWER_PUSHED || type === ACTION_TYPE.TYPE_QUESTION_PUSHED
            )
            acc[id] = questionAnswerHistory
            return acc
          },
          {}
        )

        const approvalId =  approvals.find(v=>v.concrete_approver_id === state.approval.current_user_id  && v.status)
        const approvalType = (postSocialStatus === POST_SOCIAL_STATUS.STATUS_PENDING_EXTERNAL_APPROVAL?APPROVAL_TYPE.TYPE_EXTERNAL:APPROVAL_TYPE.TYPE_INTERNAL)
        const approvalIdNonConcreteApprover =  approvals.find(v=>v.concrete_approver_id === null && v.type === approvalType && v.status )
        const lastApprovalId = approvalId?approvalId.id:(approvalIdNonConcreteApprover?approvalIdNonConcreteApprover.id:approvals[approvals.length - 1].id)


        state.postSocialsApprovalActions = {
          [id]: {
            ...approvalActionsByRounds,
            aprrovalActions: [
              ...approvalActionsByRounds.internal,
              ...approvalActionsByRounds.external
            ],
            questionAnswerHistoryByApprovalId,
            lastApprovalId
          }
        }
      } else {
        state.postSocialsApprovalActions = state.approval.post_socials.reduce(
          (accPostSocialsActions: TPostSocialsActionsMapByPostSocialId, { id, approvals,status: postSocialStatus  }) => {
            const approvalActionsByRounds = approvals.reduce(
              (
                accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
                { actions, type }
              ) => {
                // define start external approve
                const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
                  actions.map(action => ({
                    ...action,
                    type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                      ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                      action.type
                  }))
                accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
                return accPostSocialActions
              },
              { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
            )

            const questionAnswerHistoryByApprovalId = approvals.reduce(
              (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
                const questionAnswerHistory: TApprovalAction[] = actions.filter(
                  ({ type }) =>
                    type === ACTION_TYPE.TYPE_ANSWER_PUSHED ||
                    type === ACTION_TYPE.TYPE_QUESTION_PUSHED
                )
                acc[id] = questionAnswerHistory
                return acc
              },
              {}
            )

            const approvalId =  approvals.find(v=>v.concrete_approver_id === state.approval.current_user_id && v.status)
            const approvalType = postSocialStatus === POST_SOCIAL_STATUS.STATUS_PENDING_EXTERNAL_APPROVAL?APPROVAL_TYPE.TYPE_EXTERNAL:APPROVAL_TYPE.TYPE_INTERNAL
            const approvalIdNonConcreteApprover =  approvals.find(v=>v.concrete_approver_id === null && v.type === approvalType && v.status )
            const lastApprovalId = approvalId?approvalId.id:(approvalIdNonConcreteApprover?approvalIdNonConcreteApprover.id:approvals[approvals.length - 1].id)



            accPostSocialsActions[id] = {
              ...approvalActionsByRounds,
              aprrovalActions: [
                ...approvalActionsByRounds.internal,
                ...approvalActionsByRounds.external
              ],
              questionAnswerHistoryByApprovalId,
              lastApprovalId
            }
            return accPostSocialsActions
          },
          {}
        )
      }
      const { id } = state.postSocials.find(
        ({ id }: TPostSocial) => id === state.selectedPostSocialId
      )!
      state.selectedPostSocialId = id
    })
    builder.addCase(getPostApprovalInfoThunk.rejected, (state, action) => {
      state.loaders.approval.requestQty = state.loaders.approval.requestQty - 1
      if (state.loaders.approval.requestQty === 0) {
        state.loaders.approval.loading = false
      }
    })

    builder.addCase(startNextApprovalRoundThunk.pending, (state, action) => { 
      state.loaders.post.approve = true
    })
    builder.addCase(startNextApprovalRoundThunk.fulfilled, (state, action) => {
      const {
        post_social_id,
        type,
        actions,
        is_master_content: isMasterContent,
        id
      }: {
        post_social_id: number
        type: APPROVAL_TYPE
        actions: TApprovalAction[]
        is_master_content: boolean
        id: number
      } = action.payload.data

      const postSocialIndex = state.approval.post_socials.findIndex(
        ({ id }) => id === post_social_id
      )

      state.approval.post_socials[postSocialIndex].approvals.push(action.payload.data)

      state.postSocialsApprovalActions[post_social_id].aprrovalActions.push(actions[0])
      state.postSocialsApprovalActions[post_social_id][type].push(actions[0])
      state.postSocialsApprovalActions[post_social_id].questionAnswerHistoryByApprovalId[id] = []
      state.postSocialsApprovalActions[post_social_id].lastApprovalId = id
      state.postSocials = state.postSocials.map((postSocial) => {
        return post_social_id === postSocial.id || isMasterContent
          ? {
            ...postSocial,
            status: POST_SOCIAL_STATUS.STATUS_PENDING_INTERNAL_APPROVAL
          }
          : postSocial
      })
      state.post.status = POST_STATUS.STATUS_PENDING_APPROVAL
      state.loaders.post.approve = false
    })
    builder.addCase(startNextApprovalRoundThunk.rejected, (state, action) => { 
      state.loaders.post.approve = false
    })

    builder.addCase(approveCurrentRoundThunk.pending, (state, action) => {
      state.loaders.post.approve = true
      state.loaders.post.processingIds.push(action.meta.arg.post_social_id)
    })
    builder.addCase(approveCurrentRoundThunk.fulfilled, (state, action) => {
      state.loaders.post.approve = false
      state.loaders.post.processingIds = state.loaders.post.processingIds.filter(
        (id) => id !== action.meta.arg.post_social_id
      )
      state.loaders.post.processingIds = []
      state.approval.approval_plan = action.payload.data.approval_plan
      state.post.concrete_external_approver = action.payload.data.concrete_external_approver
      state.post.concrete_internal_approver = action.payload.data.concrete_external_approver
      state.approval.post_socials = action.payload.data.post_socials

      const { isMasterContent } = state.approval.approval_plan

      if (isMasterContent) {
        const { id, approvals } = state.approval.post_socials.find(
          ({ is_master_content: isMasterContent }) => isMasterContent
        )!
        const approvalActionsByRounds = approvals.reduce(
          (
            accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
            { actions, type }
          ) => {
            // define start external approve
            const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
              actions.map(action => ({
                ...action,
                type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                  ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                  action.type
              }))
            accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
            return accPostSocialActions
          },
          { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
        )

        const questionAnswerHistoryByApprovalId = approvals.reduce(
          (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
            const questionAnswerHistory: TApprovalAction[] = actions.filter(
              ({ type }) =>
                type === ACTION_TYPE.TYPE_ANSWER_PUSHED || type === ACTION_TYPE.TYPE_QUESTION_PUSHED
            )
            acc[id] = questionAnswerHistory
            return acc
          },
          {}
        )

        const lastApprovalId = approvals[approvals.length - 1].id

        state.postSocialsApprovalActions = {
          [id]: {
            ...approvalActionsByRounds,
            aprrovalActions: [
              ...approvalActionsByRounds.internal,
              ...approvalActionsByRounds.external
            ],
            questionAnswerHistoryByApprovalId,
            lastApprovalId
          }
        }

        state.postSocials = state.postSocials.map((socialPost) => {
          const updatedSocials = action.payload.data.post_socials
          const updatedSocial = updatedSocials.find(
            (updatedSocial: any) => updatedSocial.id === socialPost.id
          )

          return {
            ...socialPost,
            status: updatedSocial ? updatedSocial.status : socialPost.status
          }
        })

        state.selectedPostSocialId = state.selectedPostSocialId =
          action.payload.data.post_socials[0].id
      } else {
        state.postSocialsApprovalActions = state.approval.post_socials.reduce(
          (accPostSocialsActions: TPostSocialsActionsMapByPostSocialId, { id, approvals }) => {
            const approvalActionsByRounds = approvals.reduce(
              (
                accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
                { actions, type }
              ) => {
                // define start external approve
                const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
                  actions.map(action => ({
                    ...action,
                    type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                      ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                      action.type
                  }))
                accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
                return accPostSocialActions
              },
              { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
            )

            const questionAnswerHistoryByApprovalId = approvals.reduce(
              (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
                const questionAnswerHistory: TApprovalAction[] = actions.filter(
                  ({ type }) =>
                    type === ACTION_TYPE.TYPE_ANSWER_PUSHED ||
                    type === ACTION_TYPE.TYPE_QUESTION_PUSHED
                )
                acc[id] = questionAnswerHistory
                return acc
              },
              {}
            )

            const lastApprovalId = approvals[approvals.length - 1].id

            accPostSocialsActions[id] = {
              ...approvalActionsByRounds,
              aprrovalActions: [
                ...approvalActionsByRounds.internal,
                ...approvalActionsByRounds.external
              ],
              questionAnswerHistoryByApprovalId,
              lastApprovalId
            }
            return accPostSocialsActions
          },
          {}
        )

        state.postSocials = state.postSocials.map((socialPost) => {
          const updatedSocials = action.payload.data.post_socials
          const updatedSocial = updatedSocials.find(
            (updatedSocial: any) => updatedSocial.id === socialPost.id
          )

          return {
            ...socialPost,
            status: updatedSocial ? updatedSocial.status : socialPost.status
          }
        })

        state.selectedPostSocialId = action.payload.data.post_socials[0].id
      }
    })
    builder.addCase(approveCurrentRoundThunk.rejected, (state, action) => {
      state.loaders.post.approve = false
      state.loaders.post.processingIds = state.loaders.post.processingIds.filter(
        (id) => id !== action.meta.arg.post_social_id
      )
    })

    //unapprove post
    builder.addCase(unApproveCurrentRoundThunk.pending, (state, action) => {
      state.loaders.post.approve = true
    })

    builder.addCase(unApproveCurrentRoundThunk.fulfilled, (state, action) => {
      state.loaders.post.approve = false
    })     

    builder.addCase(unApproveCurrentRoundThunk.rejected, (state, action) => {
      state.loaders.post.approve = false
    })

    // soft delete posts
    builder.addCase(batchSoftDeletePostsThunk.pending, (state, action) => {
      state.loaders.post.delete = true
    })
    builder.addCase(batchSoftDeletePostsThunk.fulfilled, (state, action) => {
      state.loaders.post.delete = false
    })
    builder.addCase(batchSoftDeletePostsThunk.rejected, (state, action) => {
      state.loaders.post.delete = false
      state.error = action.payload
    })
    // manage social media
    builder.addCase(addMediaToTemporaryMediasThunk.pending, (state, action) => { })
    builder.addCase(
      addMediaToTemporaryMediasThunk.fulfilled,
      (state, action: PayloadAction<{ data: MEDIA_DATA_TYPE }>) => { }
    )
    builder.addCase(addMediaToTemporaryMediasThunk.rejected, (state, action) => {
      state.error = action.payload
    })
    // import canva media
    builder.addCase(importTemporaryMediaFromCanva.pending, (state, action) => { })
    builder.addCase(
      importTemporaryMediaFromCanva.fulfilled,
      (state, action: PayloadAction<{ data: MEDIA_DATA_TYPE }>) => {
        const { id: selectedId, temporaryId: selectedTemporaryId } =
          state.linkCarouselSelectedAttachmentIds
        const postSocialIndex = state.postSocials.findIndex(
          ({ id }) => state.selectedPostSocialId === id
        )
        const attachment = action.payload.data
        const { canva_design_id: canvaDesignId } = action.payload.data
        const existedAttachmentIndex = state.postSocials[postSocialIndex].attachments.findIndex(
          ({ object_data }) => object_data?.canva_design_id === canvaDesignId
        )
        const existedLinkAttachmentIndex = state.postSocials[postSocialIndex].attachments.findIndex(
          (attachment) =>
            (!!selectedId && attachment.id === selectedId) ||
            (!!selectedTemporaryId && attachment.temporaryId === selectedTemporaryId)
        )

        state.isExistChanges = true
        if (existedAttachmentIndex !== -1) {
          state.postSocials[postSocialIndex].attachments[existedAttachmentIndex] = {
            ...state.postSocials[postSocialIndex].attachments[existedAttachmentIndex],
            object_data: attachment,
            object_id: attachment.id,
            toUpdate: true
          }
        } else if (existedLinkAttachmentIndex !== -1) {
          state.postSocials[postSocialIndex].attachments[existedLinkAttachmentIndex] = {
            ...state.postSocials[postSocialIndex].attachments[existedLinkAttachmentIndex],
            object_data: attachment,
            object_id: attachment.id,
            toDeatachMedia: true
          }
        } else {
          state.postSocials[postSocialIndex].attachments.push({
            id: 0,
            temporaryId: state.postSocials[postSocialIndex].attachments.length + 1,
            post_social_id: state.postSocials[postSocialIndex].id,
            object_data: attachment,
            object_id: attachment.id,
            object_type: ATTACHMENT_TYPE.TYPE_IMAGE,
            description: ATTACHMENT_DESCRIPTION_TYPE.MEDIA,
            thumbnail_object_data: null,
            thumbnail_object_id: null,
            preview_image_id: 0,
            created_at: '',
            updated_at: '',
            toUpdate: false
          })
        }
      }
    )
    builder.addCase(importTemporaryMediaFromCanva.rejected, (state, action) => {
      state.error = action.payload
    })
    // check media restrictions to social post
    builder.addCase(checkMediaRestrictionsThunk.pending, (state, action) => { })
    builder.addCase(checkMediaRestrictionsThunk.fulfilled, (state, action: PayloadAction<any>) => {
      console.log('RESTRICTION response', action.payload)
    })
    builder.addCase(checkMediaRestrictionsThunk.rejected, (state, action) => {
      state.error = action.payload
    })
    // check media restrictions to social post
    builder.addCase(
      checkMediaRestrictionsForCopyDataToSocialPostsThunk.pending,
      (state, action) => {
        state.loaders.post.copyAttachments = true
      }
    )
    builder.addCase(
      checkMediaRestrictionsForCopyDataToSocialPostsThunk.fulfilled,
      (state, action) => {
        state.loaders.post.copyAttachments = false
        const postSocialToCopy = state.postSocials.find(
          (postSocial) => postSocial.social_network === state.selectedSocial
        )
        const allowedMedias = action.payload

        if (postSocialToCopy) {
          state.isExistChanges = true
          state.postSocials = state.postSocials.map((postSocial) => {
            const isAllowCloneFBVideoData =
              postSocial.social_network === SOCIAL_NETWORK.FACEBOOK_SOCIAL

            return postSocial.social_network === state.selectedSocial
              ? postSocial
              : {
                ...postSocial,
                isRemoveAllAttachments: true,
                message: postSocialToCopy.message,
                first_comment: postSocialToCopy.first_comment,
                message_editor_state: null,
                attachments: postSocialToCopy.attachments
                  .filter((attachment) => {
                    return (
                      !attachment.toRemove &&
                      (allowedMedias[postSocial.social_network].includes(attachment.object_id) ||
                        !attachment.object_id)
                    )
                  })
                  .map((attachment) => ({
                    ...attachment,
                    toCopy: !!attachment.id,
                    thumbnail_object_data: isAllowCloneFBVideoData
                      ? attachment.thumbnail_object_data
                      : null,
                    thumbnail_object_id: isAllowCloneFBVideoData
                      ? attachment.thumbnail_object_id
                      : null,
                    extra_fields: {
                      ...attachment.extra_fields,
                      content_tags: isAllowCloneFBVideoData
                        ? attachment.extra_fields?.[ATTACHMENT_EXTRAFIELD.CONTENT_TAGS]
                        : undefined
                    }
                  })),
                attachment_order: postSocialToCopy.attachment_order
              }
          })
        }
      }
    )
    builder.addCase(
      checkMediaRestrictionsForCopyDataToSocialPostsThunk.rejected,
      (state, action) => {
        state.loaders.post.copyAttachments = false
        state.error = action.payload
      }
    )
    // manage social media
    builder.addCase(getTemporaryMediasThunk.pending, (state, action) => { })
    builder.addCase(getTemporaryMediasThunk.fulfilled, (state, action) => { })
    builder.addCase(getTemporaryMediasThunk.rejected, (state, action) => {
      state.error = action.payload
    })
    // Delete media
    builder.addCase(deleteMediaFromTemporaryMediasThunk.pending, (state, action) => { })
    builder.addCase(deleteMediaFromTemporaryMediasThunk.fulfilled, (state, action) => { })
    builder.addCase(deleteMediaFromTemporaryMediasThunk.rejected, (state, action) => {
      state.error = action.payload
    })
    builder.addCase(showAllPostSocialNoteThunk.pending, (state, action) => { })
    builder.addCase(showAllPostSocialNoteThunk.fulfilled, (state, action) => {
      const postSocialId = action.meta.arg
      const postSocialNotes = action.payload.data
      state.postSocialNotes[postSocialId] = postSocialNotes
    })
    builder.addCase(showAllPostSocialNoteThunk.rejected, (state, action) => { })

    builder.addCase(savePostSocialNoteThunk.pending, (state, action) => {
      state.loaders.note.save = true
    })
    builder.addCase(savePostSocialNoteThunk.fulfilled, (state, action) => {
      state.loaders.note.save = false
      const postSocialId = action.meta.arg.post_social_id
      const postSocialNote = action.payload.data
      state.postSocialNotes[postSocialId].push(postSocialNote)
    })
    builder.addCase(savePostSocialNoteThunk.rejected, (state, action) => {
      state.loaders.note.save = false
    })

    builder.addCase(deletePostSocialNoteThunk.pending, (state, action) => {
      state.loaders.note.delete = true
      state.loaders.note.processingIds.push(action.meta.arg.note_id)
    })
    builder.addCase(deletePostSocialNoteThunk.fulfilled, (state, action) => {
      state.loaders.note.delete = false
      state.loaders.note.processingIds.filter((id) => id !== action.meta.arg.note_id)
      const { post_social_id: postSocialId, note_id: noteId } = action.meta.arg
      const removedNoteIndex = state.postSocialNotes[postSocialId].findIndex(
        ({ id }) => id === noteId
      )
      state.postSocialNotes[postSocialId].splice(removedNoteIndex, 1)
    })
    builder.addCase(deletePostSocialNoteThunk.rejected, (state, action) => {
      state.loaders.note.delete = false
      state.loaders.note.processingIds.filter((id) => id !== action.meta.arg.note_id)
    })

    builder.addCase(updateSocialPostNoteThunk.pending, (state, action) => { })
    builder.addCase(updateSocialPostNoteThunk.fulfilled, (state, action) => {
      const { post_social_id: postSocialId, note_id: noteId } = action.meta.arg
      const postSocialNote = action.payload.data
      const updatedNoteIndex = state.postSocialNotes[postSocialId].findIndex(
        ({ id }) => id === noteId
      )
      const tempArray = [...state.postSocialNotes[postSocialId]]
      tempArray[updatedNoteIndex] = postSocialNote
      state.postSocialNotes[postSocialId] = tempArray
    })
    builder.addCase(updateSocialPostNoteThunk.rejected, (state, action) => { })

    builder.addCase(requireApproverRevisionThunk.pending, (state, action) => {
      state.loaders.approval.requestRevision = true
    })
    builder.addCase(requireApproverRevisionThunk.fulfilled, (state, action) => {
      state.loaders.approval.requestRevision = false
      const { approval_id, post_social_id, isMasterContent } = action.meta.arg
      const revision_action = action.payload.data

      state.postSocialsApprovalActions[post_social_id].aprrovalActions.push(revision_action)
      state.postSocials = state.postSocials.map((postSocial) =>
        postSocial.id === post_social_id || isMasterContent
          ? {
            ...postSocial,
            status: POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION,
            is_approved: false
          }
          : postSocial
      )

      const actionInternalRound = state.postSocialsApprovalActions[post_social_id][
        APPROVAL_TYPE.TYPE_INTERNAL
      ].find(({ approval_id: approval_id_round }) => approval_id_round === approval_id)
      if (actionInternalRound)
        state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_INTERNAL].push(
          revision_action
        )
      else
        state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_EXTERNAL].push(
          revision_action
        )
    })
    builder.addCase(requireApproverRevisionThunk.rejected, (state, action) => {
      state.loaders.approval.requestRevision = false
    })

    builder.addCase(addStatusesToCheckboxesInRevisionBlockThunk.pending, (state, action) => { })
    builder.addCase(addStatusesToCheckboxesInRevisionBlockThunk.fulfilled, (state, action) => {
      const { approval_action_id, post_social_id } = action.meta.arg
      const checkbox_action = action.payload.data

      const actionIndex = state.postSocialsApprovalActions[post_social_id][
        APPROVAL_TYPE.TYPE_INTERNAL
      ].findIndex(({ id }) => approval_action_id === id)
      if (actionIndex !== -1) {
        const prevAction =
          state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_INTERNAL][actionIndex]
        state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_INTERNAL][actionIndex] =
        {
          ...prevAction,
          ...checkbox_action
        }
      } else {
        const actionIndex = state.postSocialsApprovalActions[post_social_id][
          APPROVAL_TYPE.TYPE_EXTERNAL
        ].findIndex(({ id }) => approval_action_id === id)
        const prevAction =
          state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_EXTERNAL][actionIndex]
        state.postSocialsApprovalActions[post_social_id][APPROVAL_TYPE.TYPE_EXTERNAL][actionIndex] =
        {
          ...prevAction,
          ...checkbox_action
        }
      }
    })
    builder.addCase(addStatusesToCheckboxesInRevisionBlockThunk.rejected, (state, action) => { })

    builder.addCase(pushQuestionByContentCreatorThunk.pending, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = true
    })
    builder.addCase(pushQuestionByContentCreatorThunk.fulfilled, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = false
      const { approval_id, post_social_id } = action.meta.arg
      const question_action = action.payload.data

      state.postSocialsApprovalActions[post_social_id].aprrovalActions.push(question_action)
      state.postSocialsApprovalActions[post_social_id].questionAnswerHistoryByApprovalId[
        approval_id
      ].push(question_action)
    })
    builder.addCase(pushQuestionByContentCreatorThunk.rejected, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = false
    })

    builder.addCase(pushAnswerByApproverThunk.pending, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = true
    })
    builder.addCase(pushAnswerByApproverThunk.fulfilled, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = false
      const { approval_id, post_social_id } = action.meta.arg
      const answer_action = action.payload.data

      state.postSocialsApprovalActions[post_social_id].aprrovalActions.push(answer_action)
      state.postSocialsApprovalActions[post_social_id].questionAnswerHistoryByApprovalId[
        approval_id
      ].push(answer_action)
    })
    builder.addCase(pushAnswerByApproverThunk.rejected, (state, action) => {
      state.loaders.approval.saveQuestionOrAnswer = false
    })

    builder.addCase(getPostsForApprovalThunk.pending, (state, action) => { })
    builder.addCase(getPostsForApprovalThunk.fulfilled, (state, action) => {
      // TODO currently disabled action to set default post to approve
      // const postToApproval: TPostForApproval | undefined = action.payload.data.find((post: TPostForApproval) =>
      //   post.latest_schedule?.date ? isFuture(new Date(post.latest_schedule.date)) : false
      // )
      // if (postToApproval) {
      //   const { latest_schedule, post_socials } = postToApproval
      //   state.post = postToApproval
      //   state.latestSchedule = latest_schedule
      //   state.postSocials = post_socials
      // }
    })
    builder.addCase(getPostsForApprovalThunk.rejected, (state, action) => { })
    // Manual publishing
    builder.addCase(publishPostManuallyThunk.pending, (state, action) => { })
    builder.addCase(publishPostManuallyThunk.fulfilled, (state, action) => {
      state.post.status = POST_STATUS.STATUS_ON_PUBLISHING
      state.postSocials.forEach(
        (socialPost) => (socialPost.status = POST_SOCIAL_STATUS.STATUS_ON_PUBLISHING)
      )
    })
    builder.addCase(publishPostManuallyThunk.rejected, (state, action) => { })
    // Manual publishing by admin
    builder.addCase(adminPublishPostManuallyThunk.pending, (state, action) => { })
    builder.addCase(adminPublishPostManuallyThunk.fulfilled, (state, action) => {
      state.post.status = POST_STATUS.STATUS_ON_PUBLISHING
      state.postSocials.forEach(
        (socialPost) => (socialPost.status = POST_SOCIAL_STATUS.STATUS_ON_PUBLISHING)
      )
    })
    builder.addCase(adminPublishPostManuallyThunk.rejected, (state, action) => { })
    // APPROVAL MODAL SLICE
    builder.addCase(postApprovalPostsThunk.pending, (state, action) => {
      state.loaders.post.approve = true
    })
    builder.addCase(postApprovalPostsThunk.rejected, (state, action) => {
      state.loaders.post.approve = false
    })
    builder.addCase(postApprovalPostsThunk.fulfilled, (state, action: PayloadAction<any>) => {
      state.loaders.post.approve = false
      const [approvalData] = action.payload.data
      state.approval.approval_plan = approvalData.approval_plan
      state.post.status = POST_STATUS.STATUS_PENDING_APPROVAL
      state.post.concrete_external_approver = approvalData.concrete_external_approver
      state.post.concrete_internal_approver = approvalData.concrete_external_approver
      state.approval.post_socials = approvalData.post_socials
      state.postSocials = state.postSocials.map((socialPost) => ({
        ...socialPost,
        status: POST_SOCIAL_STATUS.STATUS_PENDING_INTERNAL_APPROVAL
      }))

      const { isMasterContent } = approvalData.approval_plan

      if (isMasterContent) {
        const { id, approvals } = state.approval.post_socials.find(
          ({ is_master_content: isMasterContent }) => isMasterContent
        )!
        const approvalActionsByRounds = approvals.reduce(
          (
            accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
            { actions, type }
          ) => {
            // define start external approve
            const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
              actions.map(action => ({
                ...action,
                type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                  ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                  action.type
              }))
            accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
            return accPostSocialActions
          },
          { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
        )

        const questionAnswerHistoryByApprovalId = approvals.reduce(
          (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
            const questionAnswerHistory: TApprovalAction[] = actions.filter(
              ({ type }) =>
                type === ACTION_TYPE.TYPE_ANSWER_PUSHED || type === ACTION_TYPE.TYPE_QUESTION_PUSHED
            )
            acc[id] = questionAnswerHistory
            return acc
          },
          {}
        )

        const lastApprovalId = approvals[approvals.length - 1].id

        state.postSocialsApprovalActions = {
          [id]: {
            ...approvalActionsByRounds,
            aprrovalActions: [
              ...approvalActionsByRounds.internal,
              ...approvalActionsByRounds.external
            ],
            questionAnswerHistoryByApprovalId,
            lastApprovalId
          }
        }
      } else {
        state.postSocialsApprovalActions = state.approval.post_socials.reduce(
          (accPostSocialsActions: TPostSocialsActionsMapByPostSocialId, { id, approvals }) => {
            const approvalActionsByRounds = approvals.reduce(
              (
                accPostSocialActions: { [K in APPROVAL_TYPE]: TApprovalAction[] },
                { actions, type }
              ) => {
                // define start external approve
                const mappedActions = type !== APPROVAL_TYPE.TYPE_EXTERNAL ? actions :
                  actions.map(action => ({
                    ...action,
                    type: action.type === ACTION_TYPE.TYPE_APPROVAL_STARTED ?
                      ACTION_TYPE.TYPE_EXTERNAL_APPROVAL_STARTED :
                      action.type
                  }))
                accPostSocialActions[type] = accPostSocialActions[type].concat(mappedActions)
                return accPostSocialActions
              },
              { [APPROVAL_TYPE.TYPE_EXTERNAL]: [], [APPROVAL_TYPE.TYPE_INTERNAL]: [] }
            )

            const questionAnswerHistoryByApprovalId = approvals.reduce(
              (acc: { [key: number]: TApprovalAction[] }, { actions, id }) => {
                const questionAnswerHistory: TApprovalAction[] = actions.filter(
                  ({ type }) =>
                    type === ACTION_TYPE.TYPE_ANSWER_PUSHED ||
                    type === ACTION_TYPE.TYPE_QUESTION_PUSHED
                )
                acc[id] = questionAnswerHistory
                return acc
              },
              {}
            )

            const lastApprovalId = approvals[approvals.length - 1].id

            accPostSocialsActions[id] = {
              ...approvalActionsByRounds,
              aprrovalActions: [
                ...approvalActionsByRounds.internal,
                ...approvalActionsByRounds.external
              ],
              questionAnswerHistoryByApprovalId,
              lastApprovalId
            }
            return accPostSocialsActions
          },
          {}
        )
      }
      const postSocial = state.postSocials.find(
        ({ id }: TPostSocial) => id === state.selectedPostSocialId
      )
      if (postSocial) state.selectedPostSocialId = postSocial.id
    })

    //remove Attachment
    builder.addCase(removeAttachmentThunk.pending, (state, action) => {})
    builder.addCase(removeAttachmentThunk.fulfilled, (state, action) => { })
    builder.addCase(removeAttachmentThunk.rejected, (state, action) => {})
  }
})

export const {
  setViewAction,
  onFieldChangeAction,
  setSelectedPostSocialIdAction,
  setPostToApproveAction,
  setEmptyPostSocialsAction,
  setSelectedSocialAction,
  setSelectedSocialsAction,
  setPostTypeAction,
  setDateAction,
  setScheduleIdAction,
  changeAttachmentExtrafieldAction,
  resetManagePostStateAction,
  setTimeAction,
  removeAttachmentFromListAction,
  addImageToLinkAttachmentAction,
  removeImageFromLinkAttachmentAction,
  addImageToStoreAction,
  toggleIsAutopostingAction,
  setIsSchedulingPostAction,
  addFacebookVideoTagAction,
  setLinkCarouselSelectedAttachmentIdsAction,
  addLinkToSocialAttachmentAction,
  addThumbnailToAttachmentAction,
  removeThumbnailFromAttachmentAction,
  setIsAutopostingAction,
  setPostSocialExtrafieldsAction,
  updateSkipApprovalAction,
  setPostDetails
} = managePostSlice.actions
export default managePostSlice.reducer
